import { z } from "zod";
import { localStore, memoryStore } from "../../lib/store";
import { GPT_LANDING_PATH } from "@juisci/gpt-handoff";
import { isScreenshotSession } from "./screenshotSession";
import {
  isPublicBrowserPath,
  matchesSegmentPrefix,
} from "../auth/publicBrowserPaths";

/**
 * Who meets the "Install the App to Continue" screen, and whether they can walk
 * past it.
 *
 * Mobile web does not sign anyone in when the app exists for the platform
 * (decided 2026-09-25, `docs/shared-content-access/README.md`). A logged-out
 * visitor gets shared content in the browser and the install wall everywhere
 * else, the login and the signup included. The wall splits in two, because the
 * two audiences arrive for different reasons:
 *
 *  - someone who opened the app itself, or any page of it that is not shared
 *    content, has no page to read — the install screen IS the product for
 *    them, so the wall is HARD;
 *  - someone who followed a shared paper or playlist came for that page, and the
 *    API serves it without a session (verified on `/post/:slug`: the article
 *    renders logged out, with only the desktop gate over it). Walling them out
 *    of a link a colleague sent would break the share, so that wall has a door.
 *
 * Native app, desktop and signed-in readers never see it: the native shell has
 * nothing to install, and desktop has its own door (`DesktopAuthPage`).
 */

/**
 * The pages whose CONTENT the API serves with no session, so "continue in the
 * browser" lands on something real rather than an empty shell.
 *
 * INVARIANT: every path here is also a public browser path, and
 * `isPublicContentPath` enforces that rather than trusting this list to hold
 * it. The door has to open onto a page that can load itself — offering
 * "continue in the browser" for a path the logged-out guard still bounces would walk
 * the reader into `/login`, which is the failure this whole change exists to
 * end.
 *
 * `/work` and `/gpt/shared` are deliberately NOT here. They are already public
 * browser paths AND acquisition surfaces that do their own asking — the shared
 * conversation raises a `LoginSheet` on scroll — so a second wall in front of
 * them would compete with the invitation they already carry.
 */
const PUBLIC_CONTENT_PATHS = [
  "/post",
  "/playlist/public",
  // The same content reached through a Room, because a shared link carries the
  // room the sender was reading in. Same door: the visitor came for a page, not
  // for the app.
  "/room/post",
  "/room/playlist",
  // A room's resources are shareable like its publications (decided
  // 2026-09-24, `docs/shared-content-access/README.md`), from the room and
  // outside it. Same door.
  "/video",
  "/infographic",
  "/room/video",
  "/room/infographic",
  "/room/podcast",
] as const;

function isPublicContentPath(pathname: string): boolean {
  // The invariant above, enforced instead of documented. It is what keeps
  // `/post/:slug/chat` out of this set: Chat with Paper writes a conversation
  // against the reader's account, so it is not public, and `publicBrowserPaths`
  // is the one place that knows it. Restating the exception here would give it
  // two owners that must agree.
  if (!isPublicBrowserPath(pathname)) return false;
  // Whole segments, for the reason given in `publicBrowserPaths`: a bare prefix
  // reads the members-only `/room/playlists/:roomId` shelf as a shared playlist.
  return PUBLIC_CONTENT_PATHS.some((prefix) =>
    matchesSegmentPrefix(pathname, prefix)
  );
}

/**
 * WHERE the reader came in, for the install ask to report.
 *
 * The ask is the top of the acquisition funnel for every shared link, and
 * "somebody was asked to install" is worth very little without "asked off
 * WHAT". A share of one paper, a share of a playlist, a room's copy of either,
 * and an invitation are four different campaigns with four different owners,
 * and until this existed they arrived as one undifferentiated count.
 *
 * Derived from the PATHNAME rather than passed down, because the three surfaces
 * that raise the ask (the router's hard wall, the sheet, `InviteLinkPage`) have
 * only the URL in common, and a prop each of them computed would be three
 * chances to disagree.
 */
export type InstallOrigin =
  | {
      readonly kind: "article";
      readonly slug: string;
      readonly roomId: string | null;
    }
  | {
      readonly kind: "playlist";
      readonly playlistId: string;
      readonly roomId: string | null;
    }
  | {
      readonly kind: "resource";
      readonly format: "video" | "video-story" | "infographic" | "podcast";
      /** The slug of a video or an infographic, the id of a podcast. */
      readonly resourceId: string;
      readonly roomId: string | null;
    }
  | { readonly kind: "invite"; readonly inviteCode: string }
  /** `/gpt/chat/new`: the offer after a logged-out JuisciGPT answer. */
  | { readonly kind: "gpt-answer" }
  /** `/welcome`: the app itself, with no shared thing behind it. */
  | { readonly kind: "front-door" }
  | { readonly kind: "other" };

export function installOriginFor(pathname: string): InstallOrigin {
  const segments = pathname.split("/").filter((segment) => segment !== "");
  const [first, second, third, fourth, fifth] = segments;

  // `/post/:slug`
  if (first === "post" && second !== undefined) {
    return { kind: "article", slug: second, roomId: null };
  }
  // `/playlist/public/:id`
  if (first === "playlist" && second === "public" && third !== undefined) {
    return { kind: "playlist", playlistId: third, roomId: null };
  }
  // `/video/story/:slug`, then `/video/:slug`
  if (first === "video" && second === "story" && third !== undefined) {
    return {
      kind: "resource",
      format: "video-story",
      resourceId: third,
      roomId: null,
    };
  }
  if (first === "video" && second !== undefined) {
    return {
      kind: "resource",
      format: "video",
      resourceId: second,
      roomId: null,
    };
  }
  // `/infographic/:slug`
  if (first === "infographic" && second !== undefined) {
    return {
      kind: "resource",
      format: "infographic",
      resourceId: second,
      roomId: null,
    };
  }
  if (first === "room") {
    // `/room/post/:roomId/:slug`
    if (second === "post" && third !== undefined && fourth !== undefined) {
      return { kind: "article", slug: fourth, roomId: third };
    }
    // `/room/playlist/:roomId/:id`
    if (second === "playlist" && third !== undefined && fourth !== undefined) {
      return { kind: "playlist", playlistId: fourth, roomId: third };
    }
    // `/room/video/story/:roomId/:slug`, then `/room/video/:roomId/:slug`
    if (
      second === "video" &&
      third === "story" &&
      fourth !== undefined &&
      fifth !== undefined
    ) {
      return {
        kind: "resource",
        format: "video-story",
        resourceId: fifth,
        roomId: fourth,
      };
    }
    if (second === "video" && third !== undefined && fourth !== undefined) {
      return {
        kind: "resource",
        format: "video",
        resourceId: fourth,
        roomId: third,
      };
    }
    // `/room/infographic/:roomId/:slug`
    if (
      second === "infographic" &&
      third !== undefined &&
      fourth !== undefined
    ) {
      return {
        kind: "resource",
        format: "infographic",
        resourceId: fourth,
        roomId: third,
      };
    }
    // `/room/podcast/:roomId/:podcastId`
    if (second === "podcast" && third !== undefined && fourth !== undefined) {
      return {
        kind: "resource",
        format: "podcast",
        resourceId: fourth,
        roomId: third,
      };
    }
    return { kind: "other" };
  }
  // `/invite/:inviteCode`
  if (first === "invite" && second !== undefined) {
    return { kind: "invite", inviteCode: second };
  }
  if (first === "welcome") return { kind: "front-door" };
  if (pathname === GPT_LANDING_PATH) return { kind: "gpt-answer" };
  return { kind: "other" };
}

/**
 * `?appInstall=disabled` switches the wall off for the rest of the navigation.
 *
 * This is what makes the app workable in Chrome's device toolbar. Device mode
 * spoofs `navigator.userAgent`, which is the only signal any of these checks
 * read, so a developer emulating an iPhone IS a real iPhone as far as the code
 * can tell — no refinement of the test separates them, and an explicit opt-out
 * is the honest answer. `InviteLinkPage` has read this same parameter since the
 * install screen shipped; it is now read in one place for every surface.
 */
export function isInstallWallDisabledByQuery(search: string): boolean {
  return new URLSearchParams(search).get("appInstall") === "disabled";
}

/**
 * `?appInstall=force` switches the wall ON where it would have stepped aside.
 *
 * The counterpart the demo bypass created a need for: once a real desktop
 * driving the device toolbar is exempted, the gate becomes invisible in the one
 * tool a reviewer has. Without this, reviewing the wall means owning a phone,
 * and "I could not see it" reads the same as "it is broken".
 *
 * It overrides only the two bypasses that infer intent — demo mode and the
 * device-toolbar detection. It cannot conjure the wall for a reader who is
 * signed in, in the native shell, or on a real desktop viewport, because those
 * are facts about the session rather than guesses about it.
 */
export function isInstallWallForcedByQuery(search: string): boolean {
  return new URLSearchParams(search).get("appInstall") === "force";
}

/**
 * Demo mode: a Juisci salesperson showing the app from their own browser.
 *
 * `?demo=1` once, remembered on that machine. A bookmark rather than a habit —
 * a parameter you must retype is a parameter someone forgets in front of a
 * client.
 */
const demoModeStore = localStore("install-gate-demo-mode", false, z.boolean());

export function isDemoMode(search: string): boolean {
  if (new URLSearchParams(search).get("demo") === "1") {
    demoModeStore.set(true);
    return true;
  }
  return demoModeStore.get();
}

/**
 * A real desktop driving Chrome's device toolbar, rather than a real phone.
 *
 * DevTools overrides `navigator.userAgent` — the signal every other check here
 * reads — but NOT `navigator.platform`. Measured on 2026-09-22, same tab, only
 * emulation toggled: UA became `…(iPhone; CPU iPhone OS 17_5…)` while
 * `platform` stayed `MacIntel`. So the pair separates the two.
 *
 * `maxTouchPoints` is the second half, and it is there for the iPad. iPadOS
 * Safari asks for desktop sites, so a real iPad also reports `MacIntel` —
 * platform alone would quietly drop the gate on a device the app ships for.
 * Emulation reports 1 touch point, a real iPad 5, a real Mac 0.
 *
 * ⚠️ `navigator.platform` is deprecated. It works in every current browser and
 * nothing is removing it soon, but this is a convenience for demos and for
 * device-toolbar development, never the mechanism anything depends on —
 * `?demo=1` and `?appInstall=disabled` are the mechanisms, and they cannot rot.
 */
const DESKTOP_PLATFORM_PREFIXES = ["Mac", "Win", "Linux", "X11", "FreeBSD"];

export function isDesktopDrivingDeviceToolbar(): boolean {
  const onDesktopPlatform = DESKTOP_PLATFORM_PREFIXES.some((prefix) =>
    navigator.platform.startsWith(prefix)
  );
  return onDesktopPlatform && navigator.maxTouchPoints <= 1;
}

/**
 * Whether the reader has waved the sheet away on this page view.
 *
 * A MEMORY store, and that is the whole behaviour: it dies with the document,
 * so the sheet returns on every fresh load, and it survives client-side
 * navigation, so walking a shared playlist through its fourteen articles does
 * not ask fourteen times.
 */
export const installSheetDismissedStore = memoryStore(false);

export function dismissInstallSheet(): void {
  installSheetDismissedStore.set(true);
}

/** What stands in front of a logged-out mobile visitor. */
export type InstallWall =
  /** Nothing: let the route render. */
  | { readonly kind: "none" }
  /** The install screen, with no way past it. */
  | { readonly kind: "hard" }
  /**
   * The page renders, and the install sheet sits over it — the reader came for
   * this article, so they get it, and the ask arrives on top rather than
   * instead.
   */
  | { readonly kind: "sheet" };

export function installWallFor(input: {
  readonly pathname: string;
  readonly search: string;
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly isNativeApp: boolean;
  readonly isDesktop: boolean;
}): InstallWall {
  // A reader who is signed in, in the native shell, or on desktop has either
  // nothing to install or a gate of their own. `isLoading` matters because the
  // auth state is briefly unknown on boot, and walling someone during that
  // window would flash the screen at a signed-in reader.
  if (
    input.isLoading ||
    input.isAuthenticated ||
    input.isNativeApp ||
    input.isDesktop
  ) {
    return { kind: "none" };
  }
  if (isScreenshotSession()) return { kind: "none" };
  if (isInstallWallDisabledByQuery(input.search)) return { kind: "none" };
  // `force` outranks the two bypasses below it, and only those.
  if (!isInstallWallForcedByQuery(input.search)) {
    if (isDemoMode(input.search)) return { kind: "none" };
    if (isDesktopDrivingDeviceToolbar()) return { kind: "none" };
  }

  // The doors to an account. Mobile web does not sign anyone in or up when the
  // app exists for the platform: the app is where the account lives.
  if (isAuthDoorPath(input.pathname)) return { kind: "hard" };

  if (isPublicContentPath(input.pathname)) return { kind: "sheet" };

  // The other public pages open as they are: the legal pages, a shared
  // conversation, a Work landing, the partner OAuth return, the email
  // confirmation link, and an invitation, which raises its own wall
  // (`InviteLinkPage`).
  if (isPublicBrowserPath(input.pathname)) return { kind: "none" };

  // Every other page is the app. The wall stands ON the page instead of a
  // redirect to `/welcome`, so the URL the store hop records is the page the
  // reader asked for, and the app opens it after the install.
  return { kind: "hard" };
}

/**
 * The pages that open an account: the front door, the login, the signup and
 * the onboarding under it.
 *
 * A reader reaches them from a page, never for their own sake, so the install
 * hands the app the page they came from (`getPostLoginRedirectPath`) instead
 * of the door.
 *
 * `/signup/confirm-email` is not a door. It is the link in the confirmation
 * email, and it validates the address with no session, so it opens as it is.
 */
export const AUTH_DOOR_PATHS = ["/welcome", "/login", "/signup"] as const;

export function isAuthDoorPath(pathname: string): boolean {
  if (matchesSegmentPrefix(pathname, "/signup/confirm-email")) return false;
  return AUTH_DOOR_PATHS.some((prefix) =>
    matchesSegmentPrefix(pathname, prefix)
  );
}
